import type { Table } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "../ui/separator";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  isLoading: boolean;
}

export function DataTablePagination<TData>({ table, isLoading }: DataTablePaginationProps<TData>) {
  return (
    <div className="rounded-md border bg-background p-3 sm:border-0 sm:bg-transparent sm:p-0">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:px-2 sm:gap-x-6 lg:gap-x-8">
        <div className="flex min-w-0 items-center gap-x-2 sm:flex-1">
          <p className="text-sm font-normal text-muted-foreground">
            Rows <span className="hidden md:inline">per page</span>
          </p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(table.getIsAllRowsSelected() || table.getIsSomeRowsSelected()) && (
          <div className="flex-none text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} of {table.getFilteredRowModel().rows.length} row
            {table.getFilteredRowModel().rows.length === 1 ? "" : "s"} selected.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-x-4">
          <div className="flex justify-center text-sm font-normal text-muted-foreground">
            Page {table.getState().pagination.pageIndex + 1} of {isLoading ? "-" : Math.max(table.getPageCount(), 1)}
          </div>
          <Separator orientation="vertical" className="h-4 bg-muted hidden sm:flex" />
          <div className="flex justify-center text-sm font-normal text-muted-foreground">
            Total {isLoading ? "-" : table.getRowCount()}
          </div>
        </div>
        <div className="flex items-center justify-end gap-x-2 sm:ml-auto">
          <Button
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="size-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="size-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
